import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Sparkles, Loader2, ArrowLeft, AlertTriangle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public AI Visibility Audit lead-magnet form.
 * No auth. Prospect fills 6 fields → gets branded teaser PDF via email.
 */
export default function PublicAudit() {
  const navigate = useNavigate();
  const [industries, setIndustries] = useState([]);
  const [form, setForm] = useState({
    business_name: "",
    website_url: "",
    city: "",
    industry: "",
    top_customer_question: "",
    email: "",
    google_business_claimed: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [countdown, setCountdown] = useState(15);

  useEffect(() => {
    axios.get(`${API}/public/audit/industries`)
      .then(({ data }) => setIndustries(data.industries || []))
      .catch(() => {});
  }, []);

  // Auto-redirect countdown once we're in the success state.
  useEffect(() => {
    if (!done) return;
    if (countdown <= 0) { navigate("/ai-visibility"); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [done, countdown, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/audit-request`, form);
      setDone(data);
      setCountdown(15);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const emailOk = done.email_sent;
    return (
      <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center p-8">
        <div className="max-w-lg bg-[#141B2D] border border-neutral-800 p-10 text-center" data-testid="public-audit-done">
          {emailOk ? (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
              <h1 className="font-display text-3xl font-bold mb-3 text-white">You&apos;re on the list</h1>
              <p className="text-neutral-300">
                Your <strong className="text-white">AI Visibility Audit teaser</strong> is on its way to <strong className="text-white">{done.email}</strong>.
              </p>
              <p className="text-neutral-500 text-sm mt-4">
                Should arrive within 2 minutes. Check your spam / promotions folder if you don&apos;t see it.
                Reply to the email if you want the full audit — we&apos;ll take care of the rest.
              </p>
            </>
          ) : (
            <>
              <AlertTriangle className="w-14 h-14 text-amber-400 mx-auto mb-4" />
              <h1 className="font-display text-3xl font-bold mb-3 text-white">Request received</h1>
              <p className="text-neutral-300">
                We got your details but the email delivery is temporarily unavailable.
                We&apos;ll follow up manually at <strong className="text-white">{done.email}</strong> shortly.
              </p>
              <p className="text-neutral-500 text-sm mt-4">
                Prefer a faster reply? Email <a className="text-[#FF6B47] underline" href="mailto:aiaudits@clientsonramp.com">aiaudits@clientsonramp.com</a>.
              </p>
            </>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/ai-visibility")}
              data-testid="audit-back-btn"
              className="bg-[#FF6B47] hover:bg-[#E24E1B] text-white inline-flex items-center gap-2 justify-center px-5 py-3 font-semibold"
            >
              <ArrowLeft className="w-4 h-4" /> Back to AI Visibility
            </button>
            <Link
              to="/"
              data-testid="audit-home-btn"
              className="border border-neutral-700 text-neutral-300 hover:bg-neutral-800 inline-flex items-center gap-2 justify-center px-5 py-3"
            >
              Home
            </Link>
          </div>
          <div className="mt-5 text-xs font-mono uppercase tracking-widest text-neutral-500" data-testid="audit-countdown">
            Auto-return in {countdown}s
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white py-10 px-4" data-testid="public-audit-page">
      <Toaster position="top-right" richColors theme="dark" />
      <div className="max-w-2xl mx-auto">
        <Link to="/ai-visibility" className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1 mb-4" data-testid="audit-form-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="text-xs uppercase tracking-widest text-[#E24E1B] font-semibold mb-2 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Free AI Visibility Audit
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-3">
          Find out why AI is sending your customers to competitors — in 60 seconds.
        </h1>
        <p className="text-neutral-700 text-lg mb-8 max-w-xl">
          ChatGPT, Google, Gemini, and Perplexity are quietly recommending local businesses to your customers. Are they recommending yours? Get a free 1-page teaser PDF that shows your top 3 visibility gaps — no signup, no credit card.
        </p>

        <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 space-y-4 relative">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Close and return home"
            data-testid="audit-form-close"
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-neutral-500 hover:text-white hover:bg-[#0F2340] border border-neutral-200 hover:border-[#0F2340] transition rounded-full"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <Field label="Your business name">
            <input required maxLength={200} value={form.business_name}
                   onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                   data-testid="pa-business" className="lf-input" />
          </Field>
          <Field label="Website URL (or 'none')">
            <input required maxLength={400} value={form.website_url}
                   onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                   data-testid="pa-website" className="lf-input" placeholder="https:// or 'none'" />
          </Field>
          <Field label="City you serve">
            <input required maxLength={200} value={form.city}
                   onChange={(e) => setForm({ ...form, city: e.target.value })}
                   data-testid="pa-city" className="lf-input" placeholder="Los Angeles, CA" />
          </Field>
          <Field label="Industry">
            <select required value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    data-testid="pa-industry" className="lf-input">
              <option value="" disabled>Choose one…</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              <option value="Other">Other</option>
            </select>
          </Field>
          <Field label="One question your customers commonly ask you">
            <input required maxLength={500} value={form.top_customer_question}
                   onChange={(e) => setForm({ ...form, top_customer_question: e.target.value })}
                   data-testid="pa-question" className="lf-input"
                   placeholder="e.g. 'do you offer same-day service?'" />
          </Field>
          <Field label="Have you claimed your Google Business Profile?">
            <div className="flex gap-2">
              {[["yes", true], ["no", false]].map(([label, val]) => (
                <button key={label} type="button"
                        onClick={() => setForm({ ...form, google_business_claimed: val })}
                        data-testid={`pa-gbp-${label}`}
                        className={`flex-1 border px-4 py-2 text-sm ${form.google_business_claimed === val ? "border-[#E24E1B] bg-orange-50 text-[#E24E1B] font-semibold" : "border-neutral-300 text-neutral-800 bg-white hover:border-neutral-500"}`}>
                  {label.toUpperCase()}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Where should we send it?">
            <input required type="email" maxLength={200} value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="pa-email" className="lf-input" placeholder="you@yourbusiness.com" />
          </Field>
          <button type="submit" disabled={submitting}
                  data-testid="pa-submit"
                  className="w-full bg-[#0F2340] text-white text-base font-semibold py-3 hover:bg-[#E24E1B] transition disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your teaser…</> : "Get my free audit teaser →"}
          </button>
          <p className="text-xs text-neutral-500 text-center">
            No spam. We&apos;ll send you one email with the PDF. That&apos;s it.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      {children}
    </label>
  );
}
